import React, { useState, useEffect } from 'react';
import { FiSearch, FiLoader, FiInbox } from 'react-icons/fi';

import { PeriodPicker } from '@/shared/components/ui';

import {
  useAgentKnowledgeGaps,
  useGenerateGapDraft,
  useUnresolveKnowledgeGap,
} from '../../hooks/useAgentAnalytics';
import { useAnalyticsPeriod } from '../../hooks/useAnalyticsPeriod';
import type { KnowledgeGapPattern } from '../../services/agentAnalyticsApi';
import { ANALYTICS_PERIOD_SEGMENTS } from '../../utils/analytics-period';

import { GapCard } from './GapCard';
import { ResolveGapModal } from './ResolveGapModal';

interface GapsTabProps {
  agentId: string;
  /** IDs das databases do agente (do formData.databases) */
  agentDatabaseIds: string[];
  /** Callback para notificar o pai da contagem de lacunas não resolvidas */
  onCountChange?: (count: number) => void;
}

/**
 * Tab "Lacunas" dentro do AgentFormPage. Lista os gaps detectados nos
 * traces do agente com ação de resolver.
 *
 * Layout:
 * - Header: título + filtro de período + toggle "Mostrar resolvidos"
 * - Contador de gaps novos (não resolvidos)
 * - Lista de GapCards
 * - Modal ResolveGapModal (renderizado quando um gap está selecionado)
 */
export const GapsTab: React.FC<GapsTabProps> = ({ agentId, agentDatabaseIds, onCountChange }) => {
  const { value: periodValue, onChange: setPeriodValue, period, range } = useAnalyticsPeriod();
  const [showResolved, setShowResolved] = useState(false);
  const [selectedGap, setSelectedGap] = useState<KnowledgeGapPattern | null>(
    null,
  );
  /**
   * Draft (título + content) gerado pelo LLM ANTES de abrir o modal.
   * Modal abre já com isso renderizado — evita o flash de "loading
   * dentro da modal" que aparecia quando a chamada LLM acontecia depois.
   */
  const [selectedDraft, setSelectedDraft] = useState<{
    title: string;
    content: string;
  } | null>(null);
  /**
   * `gapKey` do gap atualmente em processamento (LLM gerando draft).
   * Card desse gap mostra spinner + disabled enquanto isso.
   */
  const [pendingGapKey, setPendingGapKey] = useState<string | null>(null);

  const { data, isLoading, error } = useAgentKnowledgeGaps(
    agentId,
    period,
    showResolved,
    range,
  );

  useEffect(() => {
    if (!onCountChange || isLoading) return;
    const unresolvedCount = data?.patterns?.filter(g => !g.isResolved).length ?? 0;
    onCountChange(unresolvedCount);
  }, [data, isLoading, onCountChange]);

  const unresolveMutation = useUnresolveKnowledgeGap(agentId);
  const draftMutation = useGenerateGapDraft(agentId);

  const handleResolve = async (gap: KnowledgeGapPattern): Promise<void> => {
    // Sem samples → abre modal direto com fallback heurístico
    if (gap.samples.length === 0) {
      setSelectedDraft(null);
      setSelectedGap(gap);
      return;
    }

    // Gera draft via LLM ANTES de abrir o modal — UX preferida pelo
    // user (botão vira spinner enquanto carrega, modal abre pronta)
    setPendingGapKey(gap.gapKey);
    try {
      const draft = await draftMutation.mutateAsync({
        gapLabel: gap.label,
        samples: gap.samples.map((s) => ({
          ...(s.customerMessage ? { customerMessage: s.customerMessage } : {}),
          agentSnippet: s.snippet,
        })),
      });
      setSelectedDraft(draft);
    } catch {
      // Fallback: abre o modal sem draft (modal usa buildSuggestedContent)
      setSelectedDraft(null);
    } finally {
      setPendingGapKey(null);
      setSelectedGap(gap);
    }
  };

  const handleUnresolve = async (gap: KnowledgeGapPattern): Promise<void> => {
    try {
      await unresolveMutation.mutateAsync({ gapKey: gap.gapKey });
    } catch (err) {
      void err;
    }
  };

  const handleResolved = (): void => {
    setSelectedGap(null);
    setSelectedDraft(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2
            className="text-lg font-semibold"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Lacunas de conhecimento
          </h2>
          <p
            className="text-sm mt-1"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            Perguntas que seu agente não soube responder. Resolver um gap é a
            forma mais rápida de melhorar a qualidade do atendimento.
          </p>
        </div>

        {/* Controles */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setShowResolved(!showResolved)}
            className="px-3 py-1.5 rounded-md text-sm font-medium border transition-colors"
            style={{
              backgroundColor: showResolved ? '#8b5cf6' : 'transparent',
              color: showResolved ? '#ffffff' : 'var(--theme-muted-text)',
              borderColor: showResolved ? '#8b5cf6' : 'var(--theme-card-border)',
            }}
            data-testid="agent-gaps-show-resolved"
          >
            Mostrar resolvidos
          </button>

          <PeriodPicker
            value={periodValue}
            onChange={setPeriodValue}
            segments={ANALYTICS_PERIOD_SEGMENTS}
            ariaLabel="Período das lacunas"
            className="flex-wrap"
            data-testid="agent-gaps-period"
          />
        </div>
      </div>

      {/* Contador */}
      {data && !isLoading && (
        <div
          className="rounded-lg border p-4 flex items-center gap-3"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          <FiSearch
            className="h-5 w-5"
            style={{ color: 'var(--theme-primary)' }}
          />
          <div className="flex-1">
            <div
              className="text-sm font-semibold"
              style={{ color: 'var(--theme-card-text)' }}
            >
              {data.patterns.length === 0
                ? 'Nenhuma lacuna detectada no período'
                : `${data.patterns.length} ${data.patterns.length === 1 ? 'lacuna detectada' : 'lacunas detectadas'}`}
            </div>
            <div
              className="text-xs mt-0.5"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {data.totalOccurrences} ocorrências totais · {data.resolvedCount}{' '}
              já resolvidos
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div
          className="rounded-lg border p-8 text-center"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
            color: 'var(--theme-muted-text)',
          }}
        >
          <FiLoader className="h-6 w-6 mx-auto mb-2 animate-spin" />
          <div className="text-sm">Carregando lacunas...</div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          className="rounded-lg border p-4 text-sm"
          style={{
            backgroundColor: '#fef2f2',
            borderColor: '#fecaca',
            color: '#991b1b',
          }}
        >
          Erro ao carregar lacunas: {error.message}
        </div>
      )}

      {/* Lista de gaps */}
      {!isLoading && !error && data && data.patterns.length === 0 && (
        <div
          className="rounded-lg border p-8 text-center"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          <FiInbox
            className="h-10 w-10 mx-auto mb-3"
            style={{ color: 'var(--theme-muted-text)' }}
          />
          <div
            className="text-base font-semibold mb-1"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Nenhuma lacuna{showResolved ? '' : ' em aberto'} no período
          </div>
          <p
            className="text-sm"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            {showResolved
              ? 'Ainda não há padrões de "não sei responder" detectados neste período.'
              : 'Todas as lacunas detectadas neste período já foram resolvidas. Ative "Mostrar resolvidos" para ver o histórico.'}
          </p>
        </div>
      )}

      {!isLoading && data && data.patterns.length > 0 && (
        <div className="space-y-3">
          {data.patterns.map((gap) => (
            <GapCard
              key={gap.gapKey}
              gap={gap}
              onResolve={handleResolve}
              onUnresolve={handleUnresolve}
              unresolving={unresolveMutation.isPending}
              generatingDraft={pendingGapKey === gap.gapKey}
            />
          ))}
        </div>
      )}

      {/* Modal de resolução */}
      <ResolveGapModal
        isOpen={selectedGap !== null}
        agentId={agentId}
        agentDatabaseIds={agentDatabaseIds}
        gap={selectedGap}
        initialDraft={selectedDraft}
        onClose={() => {
          setSelectedGap(null);
          setSelectedDraft(null);
        }}
        onResolved={handleResolved}
      />
    </div>
  );
};
